/**
 * ClientDataSection - Displays and allows editing of client's shared data
 *
 * Shows data shared by client via consent:
 * - Credits (existing loans)
 * - Scoring (credit score)
 * - Creditworthiness (max loan capacity)
 * - Personal data (income, employment, etc.)
 */

import { useState } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Badge,
  Button,
} from "@/shared/components/ui";
import { useSessionConsent } from "@/shared/hooks";
import {
  Shield,
  CreditCard,
  TrendingUp,
  Wallet,
  User,
  ChevronDown,
  ChevronUp,
  Clock,
  AlertCircle,
  Loader2,
  Edit2,
  RefreshCw,
} from "lucide-react";
import type {
  ConsentScopes,
  SharedCredit,
  ScoringResult,
  CreditworthinessResult,
  PersonalDataSnapshot,
} from "@/shared/types";

// ============================================
// Types
// ============================================

interface ClientDataSectionProps {
  sessionToken: string;
  onEditCredits?: (credits: SharedCredit[]) => void;
  onEditScoring?: (scoring: ScoringResult) => void;
  onEditCreditworthiness?: (creditworthiness: CreditworthinessResult) => void;
  onEditPersonalData?: (personalData: PersonalDataSnapshot) => void;
}

// ============================================
// Helper Components
// ============================================

function formatCurrency(value: number): string {
  return new Intl.NumberFormat("pl-PL", {
    style: "currency",
    currency: "PLN",
    maximumFractionDigits: 0,
  }).format(value);
}

function formatPercent(value: number): string {
  return new Intl.NumberFormat("pl-PL", {
    style: "percent",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value / 100);
}

function ConsentStatusBadge({ status }: { status: string }) {
  const variants: Record<string, "success" | "warning" | "error" | "default"> = {
    granted: "success",
    pending: "warning",
    revoked: "error",
    expired: "error",
  };

  const labels: Record<string, string> = {
    granted: "Zgoda udzielona",
    pending: "Oczekuje na zgodę",
    revoked: "Zgoda cofnięta",
    expired: "Zgoda wygasła",
  };

  return (
    <Badge variant={variants[status] || "default"}>
      {labels[status] || status}
    </Badge>
  );
}

function ScoringCategoryBadge({ category }: { category: string }) {
  const variants: Record<string, "success" | "warning" | "error" | "default"> = {
    excellent: "success",
    good: "success",
    fair: "warning",
    poor: "error",
  };

  const labels: Record<string, string> = {
    excellent: "Doskonały",
    good: "Dobry",
    fair: "Przeciętny",
    poor: "Słaby",
  };

  return (
    <Badge variant={variants[category] || "default"}>
      {labels[category] || category}
    </Badge>
  );
}

function ScopeIcon({ scope, granted }: { scope: keyof ConsentScopes; granted: boolean }) {
  const icons = {
    credits: CreditCard,
    scoring: TrendingUp,
    creditworthiness: Wallet,
    personalData: User,
  };

  const Icon = icons[scope];

  return (
    <div
      className={`w-8 h-8 rounded-full flex items-center justify-center ${
        granted ? "bg-success-100 text-success-600" : "bg-gray-100 text-gray-400"
      }`}
    >
      <Icon className="w-4 h-4" />
    </div>
  );
}

// ============================================
// Sub-components for each data type
// ============================================

function CreditsCard({
  credits,
  isEditable,
  onEdit,
}: {
  credits: SharedCredit[];
  isEditable: boolean;
  onEdit?: () => void;
}) {
  const [expanded, setExpanded] = useState(false);

  // Separate active and deleted credits
  const activeCredits = credits.filter((c) => !c.deleted);
  const deletedCredits = credits.filter((c) => c.deleted);

  // Calculate totals only from active credits
  const totalDebt = activeCredits.reduce((sum, c) => sum + c.remainingAmount, 0);
  const totalMonthly = activeCredits.reduce((sum, c) => sum + c.monthlyPayment, 0);

  return (
    <div className="border border-gray-200 rounded-lg p-4">
      <div
        className="flex items-center justify-between cursor-pointer"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center gap-3">
          <ScopeIcon scope="credits" granted />
          <div>
            <h4 className="font-medium text-gray-900">Kredyty klienta</h4>
            <p className="text-sm text-gray-500">
              {activeCredits.length} {activeCredits.length === 1 ? "kredyt" : "kredytów"}
              {deletedCredits.length > 0 && (
                <span className="text-error-500 ml-1">
                  ({deletedCredits.length} usunięt{deletedCredits.length === 1 ? "y" : "ych"})
                </span>
              )}
              {" "}| Suma: {formatCurrency(totalDebt)}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {isEditable && (
            <Button
              variant="ghost"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                onEdit?.();
              }}
            >
              <Edit2 className="w-4 h-4" />
            </Button>
          )}
          {expanded ? (
            <ChevronUp className="w-5 h-5 text-gray-400" />
          ) : (
            <ChevronDown className="w-5 h-5 text-gray-400" />
          )}
        </div>
      </div>

      {expanded && (
        <div className="mt-4 space-y-3">
          {/* Active credits */}
          {activeCredits.map((credit) => (
            <div
              key={credit.id}
              className="bg-gray-50 rounded-lg p-3 grid grid-cols-2 md:grid-cols-4 gap-3 text-sm"
            >
              <div>
                <p className="text-gray-500">Bank</p>
                <p className="font-medium">{credit.bank}</p>
              </div>
              <div>
                <p className="text-gray-500">Typ</p>
                <p className="font-medium">{credit.type}</p>
              </div>
              <div>
                <p className="text-gray-500">Pozostało</p>
                <p className="font-medium">{formatCurrency(credit.remainingAmount)}</p>
              </div>
              <div>
                <p className="text-gray-500">Rata</p>
                <p className="font-medium">{formatCurrency(credit.monthlyPayment)}</p>
              </div>
            </div>
          ))}

          {/* Deleted credits - shown with visual indication */}
          {deletedCredits.length > 0 && (
            <>
              <div className="flex items-center gap-2 text-sm text-error-500 pt-2">
                <AlertCircle className="w-4 h-4" />
                <span className="font-medium">
                  Kredyty usunięte przez klienta (nie uwzględniane w obliczeniach):
                </span>
              </div>
              {deletedCredits.map((credit) => (
                <div
                  key={credit.id}
                  className="bg-error-50 border border-error-200 rounded-lg p-3 grid grid-cols-2 md:grid-cols-4 gap-3 text-sm opacity-60"
                >
                  <div>
                    <p className="text-gray-400">Bank</p>
                    <p className="font-medium text-gray-500 line-through">{credit.bank}</p>
                  </div>
                  <div>
                    <p className="text-gray-400">Typ</p>
                    <p className="font-medium text-gray-500 line-through">{credit.type}</p>
                  </div>
                  <div>
                    <p className="text-gray-400">Pozostało</p>
                    <p className="font-medium text-gray-500 line-through">
                      {formatCurrency(credit.remainingAmount)}
                    </p>
                  </div>
                  <div className="flex flex-col">
                    <p className="text-gray-400">Status</p>
                    <Badge variant="error" className="text-xs w-fit">
                      Usunięty
                    </Badge>
                    {credit.deletedAt && (
                      <p className="text-xs text-gray-400 mt-1">
                        {new Date(credit.deletedAt).toLocaleString("pl-PL")}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </>
          )}

          <div className="flex justify-between pt-2 border-t border-gray-200 text-sm">
            <span className="text-gray-600">Suma miesięcznych rat:</span>
            <span className="font-semibold text-primary-600">
              {formatCurrency(totalMonthly)}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

function ScoringCard({
  scoring,
  isEditable,
  onEdit,
}: {
  scoring: ScoringResult;
  isEditable: boolean;
  onEdit?: () => void;
}) {
  return (
    <div className="border border-gray-200 rounded-lg p-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <ScopeIcon scope="scoring" granted />
          <div>
            <h4 className="font-medium text-gray-900">Scoring kredytowy</h4>
            <p className="text-sm text-gray-500">
              Obliczono: {new Date(scoring.calculatedAt).toLocaleDateString("pl-PL")}
            </p>
          </div>
        </div>
        {isEditable && (
          <Button variant="ghost" size="sm" onClick={onEdit}>
            <Edit2 className="w-4 h-4" />
          </Button>
        )}
      </div>

      <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-gray-50 rounded-lg p-3 text-center">
          <p className="text-2xl font-bold text-primary-600">{scoring.score}</p>
          <p className="text-sm text-gray-500">Punkty</p>
          <ScoringCategoryBadge category={scoring.category} />
        </div>
        <div className="bg-gray-50 rounded-lg p-3">
          <p className="text-sm text-gray-500">DTI (Debt-to-Income)</p>
          <p className="text-lg font-semibold">{formatPercent(scoring.dti)}</p>
        </div>
        <div className="bg-gray-50 rounded-lg p-3">
          <p className="text-sm text-gray-500">Margines budżetowy</p>
          <p className="text-lg font-semibold">{formatPercent(scoring.budgetMargin)}</p>
        </div>
        <div className="bg-gray-50 rounded-lg p-3">
          <p className="text-sm text-gray-500">Kategoria</p>
          <p className="text-lg font-semibold capitalize">{scoring.category}</p>
        </div>
      </div>
    </div>
  );
}

function CreditworthinessCard({
  creditworthiness,
  isEditable,
  onEdit,
}: {
  creditworthiness: CreditworthinessResult;
  isEditable: boolean;
  onEdit?: () => void;
}) {
  return (
    <div className="border border-gray-200 rounded-lg p-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <ScopeIcon scope="creditworthiness" granted />
          <div>
            <h4 className="font-medium text-gray-900">Zdolność kredytowa</h4>
            <p className="text-sm text-gray-500">
              Obliczono:{" "}
              {new Date(creditworthiness.calculatedAt).toLocaleDateString("pl-PL")}
            </p>
          </div>
        </div>
        {isEditable && (
          <Button variant="ghost" size="sm" onClick={onEdit}>
            <Edit2 className="w-4 h-4" />
          </Button>
        )}
      </div>

      <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-success-50 rounded-lg p-4 text-center">
          <p className="text-sm text-success-700">Maksymalna kwota kredytu</p>
          <p className="text-2xl font-bold text-success-600">
            {formatCurrency(creditworthiness.maxAmount)}
          </p>
        </div>
        <div className="bg-gray-50 rounded-lg p-4 text-center">
          <p className="text-sm text-gray-600">Maks. rata miesięczna</p>
          <p className="text-xl font-semibold">
            {formatCurrency(creditworthiness.maxMonthlyPayment)}
          </p>
        </div>
        <div className="bg-gray-50 rounded-lg p-4 text-center">
          <p className="text-sm text-gray-600">Zalecany okres</p>
          <p className="text-xl font-semibold">{creditworthiness.recommendedPeriod} lat</p>
        </div>
      </div>
    </div>
  );
}

function PersonalDataCard({
  personalData,
  isEditable,
  onEdit,
}: {
  personalData: PersonalDataSnapshot;
  isEditable: boolean;
  onEdit?: () => void;
}) {
  const employmentLabels: Record<string, string> = {
    umowa_o_prace: "Umowa o pracę",
    dzialalnosc: "Działalność gospodarcza",
    umowa_zlecenie: "Umowa zlecenie",
    umowa_o_dzielo: "Umowa o dzieło",
    emerytura: "Emerytura",
    renta: "Renta",
  };

  return (
    <div className="border border-gray-200 rounded-lg p-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <ScopeIcon scope="personalData" granted />
          <h4 className="font-medium text-gray-900">Dane osobowe</h4>
        </div>
        {isEditable && (
          <Button variant="ghost" size="sm" onClick={onEdit}>
            <Edit2 className="w-4 h-4" />
          </Button>
        )}
      </div>

      <div className="mt-4 grid grid-cols-2 md:grid-cols-3 gap-4">
        {personalData.income !== undefined && (
          <div className="bg-gray-50 rounded-lg p-3">
            <p className="text-sm text-gray-500">Dochód miesięczny</p>
            <p className="font-semibold">{formatCurrency(personalData.income)}</p>
          </div>
        )}
        {personalData.monthlyExpenses !== undefined && (
          <div className="bg-gray-50 rounded-lg p-3">
            <p className="text-sm text-gray-500">Wydatki miesięczne</p>
            <p className="font-semibold">{formatCurrency(personalData.monthlyExpenses)}</p>
          </div>
        )}
        {personalData.employmentType && (
          <div className="bg-gray-50 rounded-lg p-3">
            <p className="text-sm text-gray-500">Forma zatrudnienia</p>
            <p className="font-semibold">
              {employmentLabels[personalData.employmentType] || personalData.employmentType}
            </p>
          </div>
        )}
        {personalData.employmentDuration !== undefined && (
          <div className="bg-gray-50 rounded-lg p-3">
            <p className="text-sm text-gray-500">Staż pracy</p>
            <p className="font-semibold">{personalData.employmentDuration} mies.</p>
          </div>
        )}
        {personalData.age !== undefined && (
          <div className="bg-gray-50 rounded-lg p-3">
            <p className="text-sm text-gray-500">Wiek</p>
            <p className="font-semibold">{personalData.age} lat</p>
          </div>
        )}
        {personalData.dependents !== undefined && (
          <div className="bg-gray-50 rounded-lg p-3">
            <p className="text-sm text-gray-500">Osoby na utrzymaniu</p>
            <p className="font-semibold">{personalData.dependents}</p>
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================
// Main Component
// ============================================

export function ClientDataSection({
  sessionToken,
  onEditCredits,
  onEditScoring,
  onEditCreditworthiness,
  onEditPersonalData,
}: ClientDataSectionProps) {
  const {
    consent,
    sharedData,
    isConsentGranted,
    isLoading,
    error,
    refetch,
  } = useSessionConsent({ sessionToken });

  if (isLoading) {
    return (
      <Card>
        <CardContent className="py-8">
          <div className="flex items-center justify-center gap-3">
            <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
            <span className="text-gray-500">Ładowanie danych klienta...</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent className="py-8">
          <div className="text-center">
            <AlertCircle className="w-12 h-12 text-error-500 mx-auto mb-3" />
            <p className="text-gray-600 mb-4">Nie udało się pobrać danych klienta</p>
            <Button variant="secondary" onClick={() => refetch()}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Spróbuj ponownie
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  // No consent yet
  if (!consent || consent.status === "pending") {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="w-5 h-5" />
            Dane klienta
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-6">
            <div className="w-16 h-16 bg-warning-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Clock className="w-8 h-8 text-warning-600" />
            </div>
            <h3 className="font-semibold text-gray-900 mb-2">
              Oczekiwanie na zgodę klienta
            </h3>
            <p className="text-gray-600 text-sm max-w-md mx-auto">
              Klient musi wyrazić zgodę na udostępnienie swoich danych. Wyślij mu link do
              sesji, aby mógł zdecydować, które dane chce udostępnić.
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Consent revoked or expired
  if (consent.status === "revoked" || consent.status === "expired") {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="w-5 h-5" />
            Dane klienta
            <ConsentStatusBadge status={consent.status} />
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-6">
            <div className="w-16 h-16 bg-error-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="w-8 h-8 text-error-600" />
            </div>
            <h3 className="font-semibold text-gray-900 mb-2">
              {consent.status === "revoked"
                ? "Klient cofnął zgodę"
                : "Zgoda wygasła"}
            </h3>
            <p className="text-gray-600 text-sm max-w-md mx-auto">
              {consent.status === "revoked"
                ? "Klient cofnął zgodę na udostępnianie danych. Nie masz już dostępu do jego danych."
                : "Zgoda na udostępnianie danych wygasła. Poproś klienta o ponowne wyrażenie zgody."}
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Consent granted - show data
  const scopes = consent.sharedScopes;
  const hasAnyData = sharedData && (
    sharedData.credits ||
    sharedData.scoring ||
    sharedData.creditworthiness ||
    sharedData.personalData
  );

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Shield className="w-5 h-5" />
            Dane klienta
            <ConsentStatusBadge status={consent.status} />
          </CardTitle>
          {sharedData?.lastSyncedAt && (
            <span className="text-xs text-gray-500">
              Ostatnia synchronizacja:{" "}
              {new Date(sharedData.lastSyncedAt).toLocaleString("pl-PL")}
            </span>
          )}
        </div>

        {/* Shared scopes summary */}
        {scopes && (
          <div className="flex flex-wrap gap-2 mt-3">
            {scopes.credits && (
              <Badge variant="success" className="text-xs">
                <CreditCard className="w-3 h-3 mr-1" />
                Kredyty
              </Badge>
            )}
            {scopes.scoring && (
              <Badge variant="success" className="text-xs">
                <TrendingUp className="w-3 h-3 mr-1" />
                Scoring
              </Badge>
            )}
            {scopes.creditworthiness && (
              <Badge variant="success" className="text-xs">
                <Wallet className="w-3 h-3 mr-1" />
                Zdolność
              </Badge>
            )}
            {scopes.personalData && (
              <Badge variant="success" className="text-xs">
                <User className="w-3 h-3 mr-1" />
                Dane osobowe
              </Badge>
            )}
          </div>
        )}
      </CardHeader>

      <CardContent className="space-y-4">
        {!hasAnyData ? (
          <div className="text-center py-6 text-gray-500">
            <p>Klient nie udostępnił jeszcze żadnych danych.</p>
          </div>
        ) : (
          <>
            {/* Credits */}
            {sharedData?.credits && sharedData.credits.length > 0 && (
              <CreditsCard
                credits={sharedData.credits}
                isEditable={isConsentGranted}
                onEdit={() => onEditCredits?.(sharedData.credits!)}
              />
            )}

            {/* Scoring */}
            {sharedData?.scoring && (
              <ScoringCard
                scoring={sharedData.scoring}
                isEditable={isConsentGranted}
                onEdit={() => onEditScoring?.(sharedData.scoring!)}
              />
            )}

            {/* Creditworthiness */}
            {sharedData?.creditworthiness && (
              <CreditworthinessCard
                creditworthiness={sharedData.creditworthiness}
                isEditable={isConsentGranted}
                onEdit={() => onEditCreditworthiness?.(sharedData.creditworthiness!)}
              />
            )}

            {/* Personal Data */}
            {sharedData?.personalData && (
              <PersonalDataCard
                personalData={sharedData.personalData}
                isEditable={isConsentGranted}
                onEdit={() => onEditPersonalData?.(sharedData.personalData!)}
              />
            )}
          </>
        )}

        {/* Data version info */}
        {sharedData && (
          <div className="flex items-center justify-between text-xs text-gray-400 pt-2 border-t border-gray-100">
            <span>Wersja danych: {sharedData.version}</span>
            {sharedData.lastEditedBy && (
              <span>
                Ostatnia edycja:{" "}
                {sharedData.lastEditedBy === "advisor" ? "Doradca" : "Klient"}
              </span>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
